import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useThemeStore } from '@/app/providers/themeStore';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { semantic, typography, shadow, radius } from '@/shared/theme/tokens';
import { ROUTES } from '@/shared/config/constants';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { useSidebar } from '@/app/routes/AuthenticatedLayout';

const TOPBAR_H = 56;

export function AppTopbar() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { preference, setPreference } = useThemeStore();
  const c = useThemeColors();
  const isDark = useIsDark();
  const { isMobile } = useBreakpoint();
  const { toggle: toggleSidebar } = useSidebar();
  const [profileOpen, setProfileOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = getInitials(user?.name ?? user?.email ?? 'U');
  const displayName = user?.name ?? user?.email?.split('@')[0] ?? 'User';

  function handleLogout() {
    useAuthStore.getState().clearAuth();
    navigate(ROUTES.LOGIN);
  }

  return (
    <header style={{
      position: 'fixed',
      top: 0, left: 0, right: 0,
      height: TOPBAR_H,
      background: c.bg,
      borderBottom: `1px solid ${c.borderSubtle}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px',
      zIndex: 300,
      boxShadow: isDark ? '0 1px 0 rgba(255,255,255,0.05)' : '0 1px 0 rgba(30,67,150,0.06)',
      fontFamily: typography.fontFamily.body,
    }}>
      {/* LEFT: Hamburger (mobile) + Logo + Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        {isMobile && (
          <button
            onClick={toggleSidebar}
            style={{
              width: 34, height: 34, borderRadius: radius.md,
              background: 'transparent', border: `1px solid ${c.divider}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: c.textSubtle }}>menu</span>
          </button>
        )}
        <div style={{
          width: 30, height: 30, borderRadius: radius.md,
          background: semantic.brandGrad,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: shadow.topbar, flexShrink: 0,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#ffffff', fontVariationSettings: "'FILL' 1" }}>
            bolt
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading, letterSpacing: typography.letterSpacing.normal, whiteSpace: 'nowrap' }}>
            Industrial AutoPilot
          </span>
          {!isMobile && <span style={{ fontSize: typography.fontSize.xs, color: c.textStrong, letterSpacing: typography.letterSpacing.normal }}>by Ripik AI</span>}
        </div>
      </div>

      {/* RIGHT: Theme toggle + Notifications + User chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {!isMobile && <ThemeToggle preference={preference} onSelect={setPreference} c={c} />}

        {/* User Chip */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setProfileOpen((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '5px 10px 5px 5px', borderRadius: radius.lg,
              border: `1px solid ${profileOpen ? 'rgba(62,181,231,0.4)' : c.divider}`,
              background: profileOpen ? c.bgHover : 'transparent',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <div style={{
              width: 26, height: 26, borderRadius: radius.md,
              background: 'linear-gradient(135deg, #202475, #3EB5E7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: '#ffffff', flexShrink: 0,
            }}>
              {initials}
            </div>
            {!isMobile && (
              <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>
                {displayName}
              </span>
            )}
            <span className="material-symbols-outlined" style={{
              fontSize: 15, color: c.textSubtle,
              transform: profileOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
            }}>expand_more</span>
          </button>

          {profileOpen && (
            <ProfileDropdown
              c={c}
              onLogout={() => { setProfileOpen(false); handleLogout(); }}
            />
          )}
        </div>
      </div>
    </header>
  );
}

const THEME_CYCLE = ['auto', 'light', 'dark'];
const THEME_META = {
  auto:  { icon: 'brightness_auto', label: 'Auto' },
  light: { icon: 'light_mode',      label: 'Light' },
  dark:  { icon: 'dark_mode',       label: 'Dark' },
};

function ThemeToggle({ preference, onSelect, c }) {
  const [hover, setHover] = useState(false);
  const meta = THEME_META[preference] ?? THEME_META.auto;

  function cycle() {
    const idx = THEME_CYCLE.indexOf(preference);
    onSelect(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]);
  }

  return (
    <button
      onClick={cycle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`Theme: ${meta.label} — click to cycle`}
      style={{
        display: 'flex', alignItems: 'center', gap: '5px',
        padding: '5px 10px', borderRadius: radius.lg,
        border: `1px solid ${hover ? 'rgba(62,181,231,0.35)' : c.divider}`,
        background: hover ? c.bgHover : 'transparent',
        cursor: 'pointer', transition: 'all 0.15s',
        fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold,
        color: c.textSubtle,
        fontFamily: typography.fontFamily.body,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 14, fontVariationSettings: "'FILL' 1", color: hover ? c.textActive : c.textSubtle, transition: 'color 0.15s' }}>
        {meta.icon}
      </span>
      <span style={{ color: hover ? c.textActive : c.textSubtle, transition: 'color 0.15s' }}>{meta.label}</span>
    </button>
  );
}

function ProfileDropdown({ c, onLogout }) {
  const [hoverLogout, setHoverLogout] = useState(false);

  return (
    <div style={{
      position: 'absolute', top: 'calc(100% + 6px)', right: 0,
      minWidth: '180px', background: c.bgMuted,
      border: `1px solid ${c.borderSubtle}`, borderRadius: radius.xl,
      boxShadow: c.shadow ?? '0 8px 32px rgba(30,67,150,0.12)', overflow: 'hidden', zIndex: 400,
    }}>
      <button
        onClick={onLogout}
        onMouseEnter={() => setHoverLogout(true)}
        onMouseLeave={() => setHoverLogout(false)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
          padding: '11px 14px',
          background: hoverLogout ? semantic.errorContainer : 'transparent',
          border: 'none', cursor: 'pointer', fontSize: typography.fontSize.md,
          color: hoverLogout ? semantic.error : c.textPrimary,
          fontFamily: typography.fontFamily.body,
          transition: 'all 0.12s', textAlign: 'left',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: hoverLogout ? semantic.error : c.textSubtle }}>logout</span>
        Logout
      </button>
    </div>
  );
}

function getInitials(nameOrEmail) {
  const parts = nameOrEmail.split(/[\s@]/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0]?.[0] ?? 'U').toUpperCase();
}
